import React, { useEffect, useState, useCallback } from "react";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import { useNavigate, Link } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import {
  FiArrowLeft,
  FiChevronRight,
  FiCopy,
  FiSearch,
  FiX,
  FiEye,
  FiRefreshCw,
  FiShield,
  FiUsers,
  FiSmartphone,
  FiDollarSign,
  FiCheck,
  FiLayers,
} from "react-icons/fi";

const baseUrl = process.env.REACT_APP_API_BASE_URL;

const avatarColors = [
  "bg-[#ffffcc] text-[#999900]",
  "bg-purple-100 text-purple-600",
  "bg-green-100 text-green-600",
  "bg-orange-100 text-orange-600",
  "bg-rose-100 text-rose-600",
  "bg-teal-100 text-teal-600",
];
const getAvatarColor = (name = "") =>
  avatarColors[(name || "U").charCodeAt(0) % avatarColors.length];

const Spinner = ({ sm }) => (
  <div
    className={`border-2 border-t-transparent rounded-full animate-spin ${
      sm ? "w-3.5 h-3.5 border-current" : "w-6 h-6 border-[#ffff00]"
    }`}
  />
);

const ITEMS_PER_PAGE = 15;

const DuplicateAccount = () => {
  const navigate = useNavigate();
  const [allDuplicateGroups, setAllDuplicateGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [totalScannedUsers, setTotalScannedUsers] = useState(0);

  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("count"); // "count" | "coins" | "date"
  const [sortOrder, setSortOrder] = useState("desc"); // "asc" | "desc"
  const [currentPage, setCurrentPage] = useState(1);
  const [copiedUuid, setCopiedUuid] = useState(null);

  // Fetch all users and group by matching UUID
  const fetchAndGroupUsers = useCallback(async (isManualScan = false) => {
    if (isManualScan) {
      setScanning(true);
    } else {
      setLoading(true);
    }

    try {
      const token = localStorage.getItem("token");

      // 1. Try dedicated backend endpoint first
      let loadedFromBackend = false;
      try {
        const backendRes = await fetch(`${baseUrl}/users/duplicate-accounts`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        const backendData = await backendRes.json();
        if (backendData.status === true && Array.isArray(backendData.data)) {
          const list = backendData.data.map((item) => ({
            uuid: item.uuid,
            count: item.count || (item.accounts ? item.accounts.length : 0),
            primaryUser: item.primaryUser || (item.accounts ? item.accounts[0] : {}),
            accounts: item.accounts || [],
            totalCoins:
              item.totalCoins != null
                ? parseFloat(item.totalCoins)
                : (item.accounts || []).reduce((sum, u) => sum + (parseFloat(u.coin) || 0), 0),
            createdAt: item.createdAt || (item.accounts && item.accounts[0] ? item.accounts[0].createdAt : null),
          }));
          setAllDuplicateGroups(list);
          setTotalScannedUsers(backendData.pagination?.totalUsers || list.reduce((s, g) => s + g.count, 0));
          loadedFromBackend = true;
          if (isManualScan) {
            toast.success(`Loaded ${list.length} duplicate UUID group(s) from server!`);
          }
        }
      } catch (backendErr) {
        // Fallback to client scanning
      }

      if (loadedFromBackend) {
        setLoading(false);
        setScanning(false);
        return;
      }

      // 2. Fallback: Scan from users/list with optimized 1000-per-batch loading
      let allUsers = [];
      const perPage = 1000;

      // First batch
      const firstRes = await fetch(
        `${baseUrl}/users/list?status=All&page=1&perPage=${perPage}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      const firstData = await firstRes.json();

      if (firstData.status === true && Array.isArray(firstData.data)) {
        allUsers = [...firstData.data];
        const total = firstData.pagination?.total || firstData.data.length;
        const totalPages = Math.ceil(total / perPage);

        // Fetch remaining in parallel batches of 4
        if (totalPages > 1) {
          const remainingPages = [];
          for (let p = 2; p <= totalPages; p++) {
            remainingPages.push(p);
          }

          const CHUNK_SIZE = 4;
          for (let i = 0; i < remainingPages.length; i += CHUNK_SIZE) {
            const chunk = remainingPages.slice(i, i + CHUNK_SIZE);
            const chunkPromises = chunk.map((pageNo) =>
              fetch(
                `${baseUrl}/users/list?status=All&page=${pageNo}&perPage=${perPage}`,
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                  },
                }
              ).then((r) => r.json())
            );

            const chunkResults = await Promise.all(chunkPromises);
            for (const res of chunkResults) {
              if (res.status === true && Array.isArray(res.data)) {
                allUsers.push(...res.data);
              }
            }
          }
        }
      } else {
        throw new Error(firstData.message || "Failed to fetch users");
      }

      setTotalScannedUsers(allUsers.length);

      // Group users by valid UUID
      const uuidMap = {};
      allUsers.forEach((user) => {
        const uuid = (user.uuid || "").trim();
        // Ignore null, empty, or unknown UUIDs
        if (
          uuid &&
          uuid !== "" &&
          uuid !== "null" &&
          uuid !== "undefined" &&
          uuid !== "unknown" &&
          uuid !== "-"
        ) {
          if (!uuidMap[uuid]) {
            uuidMap[uuid] = [];
          }
          uuidMap[uuid].push(user);
        }
      });

      // Filter groups where UUID appears 2 or more times
      const duplicateList = Object.keys(uuidMap)
        .filter((uuid) => uuidMap[uuid].length > 1)
        .map((uuid) => {
          const accounts = uuidMap[uuid];
          // Sort accounts inside group (oldest created first)
          accounts.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
          const primaryUser = accounts[0];
          const totalCoins = accounts.reduce(
            (sum, u) => sum + (parseFloat(u.coin) || 0),
            0
          );
          return {
            uuid,
            count: accounts.length,
            primaryUser,
            accounts,
            totalCoins,
            createdAt: primaryUser.createdAt || accounts[0].createdAt,
          };
        });

      setAllDuplicateGroups(duplicateList);

      if (isManualScan) {
        toast.success(
          `Scanned ${allUsers.length} users. Found ${duplicateList.length} duplicate UUID group(s)!`
        );
      }
    } catch (err) {
      toast.error(err.message || "Error scanning duplicate users");
    } finally {
      setLoading(false);
      setScanning(false);
    }
  }, []);

  useEffect(() => {
    fetchAndGroupUsers(false);
  }, [fetchAndGroupUsers]);

  const handleScanDuplicates = () => {
    fetchAndGroupUsers(true);
  };

  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedUuid(text);
    toast.success("UUID copied to clipboard");
    setTimeout(() => setCopiedUuid(null), 2000);
  };

  const handleViewDetails = (group) => {
    if (!group?.uuid) {
      toast.error("Invalid device UUID");
      return;
    }
    navigate(`/duplicate-account/${encodeURIComponent(group.uuid)}`, {
      state: { group },
    });
  };

  const formatDate = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Filter by search
  const filteredGroups = allDuplicateGroups.filter((group) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    const matchesUuid = group.uuid.toLowerCase().includes(term);
    const matchesAccount = group.accounts.some(
      (acc) =>
        (acc.username && acc.username.toLowerCase().includes(term)) ||
        (acc.email && acc.email.toLowerCase().includes(term)) ||
        (acc.userId && String(acc.userId).includes(term))
    );
    return matchesUuid || matchesAccount;
  });

  // Sort
  const sortedGroups = [...filteredGroups].sort((a, b) => {
    let diff = 0;
    if (sortField === "count") {
      diff = a.count - b.count;
    } else if (sortField === "coins") {
      diff = a.totalCoins - b.totalCoins;
    } else if (sortField === "date") {
      diff = new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
    }
    return sortOrder === "asc" ? diff : -diff;
  });

  // Pagination
  const totalPages = Math.max(1, Math.ceil(sortedGroups.length / ITEMS_PER_PAGE));
  const paginatedGroups = sortedGroups.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("desc");
    }
    setCurrentPage(1);
  };

  // Summary Metrics
  const totalDuplicateDevices = allDuplicateGroups.length;
  const totalMultiAccounts = allDuplicateGroups.reduce((sum, g) => sum + g.count, 0);
  const totalCoinsInDuplicates = allDuplicateGroups.reduce((sum, g) => sum + g.totalCoins, 0);
  const maxSharingCount = allDuplicateGroups.reduce(
    (max, g) => Math.max(max, g.count),
    0
  );

  return (
    <div className="flex bg-gray-100 min-h-screen">
      <Toaster position="top-right" />
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />
        <div className="p-5 sm:p-8 space-y-6">
          {/* Breadcrumbs & Header */}
          <div className="space-y-3">
            <div className="flex items-center flex-wrap gap-1.5 text-xs text-gray-500 font-medium">
              <Link
                to="/user"
                className="hover:text-black hover:underline transition flex items-center gap-1"
              >
                Users
              </Link>
              <FiChevronRight size={12} className="text-gray-400 flex-shrink-0" />
              <span className="text-gray-900 font-bold bg-[#ffffcc] px-2 py-0.5 rounded">
                Duplicate Accounts
              </span>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    if (window.history.length > 1) {
                      navigate(-1);
                    } else {
                      navigate("/user");
                    }
                  }}
                  className="p-2.5 bg-white hover:bg-gray-50 border border-gray-200 rounded-xl text-gray-700 hover:text-black transition shadow-xs cursor-pointer flex items-center justify-center"
                  title="Back to Previous Page"
                  aria-label="Back"
                >
                  <FiArrowLeft size={18} />
                </button>
                <div>
                  <h1 className="text-2xl font-black text-gray-900 tracking-tight">
                    Duplicate Accounts
                  </h1>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Accounts detected with identical device UUIDs
                  </p>
                </div>
              </div>

              <button
                onClick={handleScanDuplicates}
                disabled={scanning || loading}
                className="flex items-center justify-center gap-2 bg-[#ffff00] hover:bg-[#e6e600] disabled:opacity-60 text-black font-semibold px-4 py-2.5 rounded-xl shadow-sm transition text-sm self-start sm:self-auto cursor-pointer"
                title="Scan all users and group matching UUIDs"
              >
                <FiRefreshCw size={15} className={scanning ? "animate-spin" : ""} />
                {scanning ? "Scanning UUIDs..." : "Check Duplicate UUIDs"}
              </button>
            </div>
          </div>

          {/* Stats Bar */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex items-center gap-3.5">
              <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center text-red-500 flex-shrink-0">
                <FiSmartphone size={20} />
              </div>
              <div>
                <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">
                  Duplicate Devices
                </p>
                <p className="text-xl font-bold text-gray-800">
                  {totalDuplicateDevices.toLocaleString()}
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex items-center gap-3.5">
              <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center text-orange-500 flex-shrink-0">
                <FiUsers size={20} />
              </div>
              <div>
                <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">
                  Multi-Accounts
                </p>
                <p className="text-xl font-bold text-gray-800">
                  {totalMultiAccounts.toLocaleString()}
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex items-center gap-3.5">
              <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center text-purple-600 flex-shrink-0">
                <FiLayers size={20} />
              </div>
              <div>
                <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">
                  Max Sharing on 1 Device
                </p>
                <p className="text-xl font-bold text-gray-800">
                  {maxSharingCount} {maxSharingCount === 1 ? "Account" : "Accounts"}
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex items-center gap-3.5">
              <div className="w-10 h-10 rounded-xl bg-yellow-50 flex items-center justify-center text-[#999900] flex-shrink-0">
                <FiDollarSign size={20} />
              </div>
              <div>
                <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">
                  Total Duplicate Coins
                </p>
                <p className="text-xl font-bold text-gray-800">
                  {totalCoinsInDuplicates.toFixed(2)}
                </p>
              </div>
            </div>
          </div>

          {/* Preservation & Safety Banner */}
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex items-start gap-3 text-sm text-blue-800">
            <FiShield size={18} className="text-blue-500 flex-shrink-0 mt-0.5" />
            <div>
              <span className="font-semibold">Observation & Preservation Mode:</span> Users sharing the same device UUID are automatically grouped here from registered user records. In accordance with system policy, these accounts are safely preserved and are not deleted or banned.
            </div>
          </div>

          {/* Main Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            {/* Toolbar */}
            <div className="px-5 py-4 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-700">
                  Duplicate Device Groups
                </h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  Showing {sortedGroups.length} device{sortedGroups.length === 1 ? "" : "s"} with multiple accounts (scanned from {totalScannedUsers} users)
                </p>
              </div>

              {/* Search Bar */}
              <div className="relative w-full sm:w-80">
                <FiSearch
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  size={14}
                />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  placeholder="Search username, email, UUID..."
                  className="w-full pl-8 pr-8 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#ffff00]"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchTerm("");
                      setCurrentPage(1);
                    }}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <FiX size={13} />
                  </button>
                )}
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              {loading ? (
                <div className="flex justify-center items-center py-24 gap-3 text-gray-400 text-sm">
                  <Spinner /> Scanning and checking duplicate UUIDs...
                </div>
              ) : sortedGroups.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                  <FiCopy size={36} className="text-gray-300 mb-3" />
                  <p className="text-sm font-medium">No duplicate accounts found</p>
                  {searchTerm && (
                    <p className="text-xs mt-1">Try a different search keyword</p>
                  )}
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left text-xs text-gray-400 uppercase tracking-wide">
                      <th className="px-5 py-3 w-10">#</th>
                      <th className="px-5 py-3">Primary Account</th>
                      <th className="px-5 py-3 hidden md:table-cell">Device UUID</th>
                      <th className="px-5 py-3 text-right cursor-pointer" onClick={() => toggleSort("coins")}>
                        <div className="flex items-center justify-end gap-1">
                          Total Coins
                          <span className="text-gray-400 text-[10px]">
                            {sortField === "coins" ? (sortOrder === "asc" ? "▲" : "▼") : "↕"}
                          </span>
                        </div>
                      </th>
                      <th className="px-5 py-3 text-center cursor-pointer" onClick={() => toggleSort("count")}>
                        <div className="flex items-center justify-center gap-1">
                          Accounts
                          <span className="text-gray-400 text-[10px]">
                            {sortField === "count" ? (sortOrder === "asc" ? "▲" : "▼") : "↕"}
                          </span>
                        </div>
                      </th>
                      <th className="px-5 py-3 hidden lg:table-cell cursor-pointer" onClick={() => toggleSort("date")}>
                        <div className="flex items-center gap-1">
                          First Joined
                          <span className="text-gray-400 text-[10px]">
                            {sortField === "date" ? (sortOrder === "asc" ? "▲" : "▼") : "↕"}
                          </span>
                        </div>
                      </th>
                      <th className="px-5 py-3 text-center w-28">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {paginatedGroups.map((group, index) => {
                      const primary = group.primaryUser || {};
                      const initials = (primary.username || "U")[0].toUpperCase();
                      const avatarCls = getAvatarColor(primary.username || "");
                      const rowNumber = (currentPage - 1) * ITEMS_PER_PAGE + index + 1;

                      return (
                        <tr key={group.uuid} className="hover:bg-gray-50 transition">
                          <td className="px-5 py-3.5 text-gray-400 text-xs">
                            {rowNumber}
                          </td>
                          <td className="px-5 py-3.5">
                            <div className="flex items-center gap-3">
                              <div
                                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${avatarCls}`}
                              >
                                {initials}
                              </div>
                              <div className="min-w-0">
                                <p className="font-medium text-gray-800 truncate max-w-[150px]">
                                  {primary.username || "—"}
                                </p>
                                <p className="text-xs text-gray-400 truncate max-w-[150px]">
                                  {primary.email || "—"}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-3.5 hidden md:table-cell">
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-xs text-gray-500 truncate max-w-[180px]">
                                {group.uuid}
                              </span>
                              <button
                                onClick={() => copyToClipboard(group.uuid)}
                                className="text-gray-400 hover:text-gray-700 transition p-1 rounded"
                                title="Copy UUID"
                              >
                                {copiedUuid === group.uuid ? (
                                  <FiCheck size={12} className="text-green-600" />
                                ) : (
                                  <FiCopy size={12} />
                                )}
                              </button>
                            </div>
                          </td>
                          <td className="px-5 py-3.5 text-right font-semibold text-gray-700 whitespace-nowrap">
                            {group.totalCoins.toFixed(2)}
                          </td>
                          <td className="px-5 py-3.5 text-center">
                            <span
                              className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-bold ${
                                group.count > 2
                                  ? "bg-red-100 text-red-700"
                                  : "bg-orange-100 text-orange-700"
                              }`}
                            >
                              {group.count} Accounts
                            </span>
                          </td>
                          <td className="px-5 py-3.5 text-gray-500 text-xs whitespace-nowrap hidden lg:table-cell">
                            {formatDate(group.createdAt)}
                          </td>
                          <td className="px-5 py-3.5 text-center">
                            <button
                              onClick={() => handleViewDetails(group)}
                              className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#ffffcc] text-[#999900] hover:bg-[#ffff00] hover:text-black transition mx-auto shadow-sm"
                            >
                              <FiEye size={13} /> View ({group.count})
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination */}
            {!loading && sortedGroups.length > ITEMS_PER_PAGE && (
              <div className="px-5 py-4 border-t border-gray-100 flex flex-wrap items-center justify-between gap-3">
                <p className="text-xs text-gray-400">
                  Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1}–
                  {Math.min(currentPage * ITEMS_PER_PAGE, sortedGroups.length)} of{" "}
                  {sortedGroups.length} duplicate devices
                </p>
                <div className="flex items-center gap-1.5">
                  <button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    className="px-3 py-1.5 text-sm border rounded-lg bg-white hover:bg-gray-50 disabled:opacity-40"
                  >
                    &laquo;
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .slice(
                      Math.max(0, currentPage - 3),
                      Math.min(totalPages, currentPage + 2)
                    )
                    .map((pg) => (
                      <button
                        key={pg}
                        onClick={() => setCurrentPage(pg)}
                        className={`px-3 py-1.5 text-sm border rounded-lg transition ${
                          currentPage === pg
                            ? "bg-[#ffff00] text-black border-[#ffff00] font-bold"
                            : "bg-white hover:bg-gray-50"
                        }`}
                      >
                        {pg}
                      </button>
                    ))}
                  <button
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                    className="px-3 py-1.5 text-sm border rounded-lg bg-white hover:bg-gray-50 disabled:opacity-40"
                  >
                    &raquo;
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DuplicateAccount;

